/**
 * BPR (Business Process Review) API client
 *
 * Thin typed wrapper over the /api/sales/bpr routes (apps/api/src/routes/bpr.ts).
 * Every response wraps its payload as { data: ... }; helpers here unwrap it so
 * callers receive the @protoss/shared DTOs directly.
 */

import type {
  BprCreateEngagementRequest,
  BprCreateFindingRequest,
  BprCreateNoteRequest,
  BprCreateOverrideRequest,
  BprCreateRecommendationRequest,
  BprCreateSessionRequest,
  BprDiagramRecord,
  BprDiscoveryNote,
  BprEngagement,
  BprFeedbackRequest,
  BprFinding,
  BprFrameworkContent,
  BprFrameworkStatus,
  BprFrameworkVersionDetail,
  BprFrameworkVersionSummary,
  BprGenerateFrameworkRequest,
  BprGraphAreaPriorityResult,
  BprGraphDiagram,
  BprGraphPriorityResult,
  BprGraphVersionDetail,
  BprOutput,
  BprOutputKind,
  BprOverride,
  BprRecommendation,
  BprRegisterSourceRequest,
  BprSession,
  BprSource,
  BprSynthesisRunSummary,
  BprUpdateEngagementRequest,
  BprUpdateFindingRequest,
  BprUpdateRecommendationRequest,
  BprUpdateSessionRequest,
  BprUpdateSourceRequest,
} from '@protoss/shared';
import { ApiError, apiFetch } from '../../../lib/apiClient';

const BASE = '/api/sales/bpr';

/** True only for a REAL 404 - auth failures and 5xx must surface, not read as "absent". */
function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.statusCode === 404;
}

/** Result of pinning the current effective framework onto an engagement. */
export interface BprPinFrameworkResult {
  engagement: BprEngagement;
  frameworkStatus: string;
}

/** The workspace-effective framework (approved version + active overrides applied). */
export interface BprResolvedFramework {
  versionUuid: string;
  status: BprFrameworkStatus;
  content: BprFrameworkContent;
  appliedOverrideUuids: string[];
}

/** Accepted (202) framework-generation dispatch acknowledgement. */
export interface BprGenerateFrameworkResult {
  runUuid?: string;
  status?: string;
  [key: string]: unknown;
}

/** D1 diagram payload: the two render-agnostic views + the version they came from. */
export type BprGraphDiagramResult = BprGraphDiagram & { graphVersionUuid: string };

/** Stage-C priorities payload for the latest cached graph version. */
export type BprGraphPrioritiesResult = BprGraphPriorityResult & { graphVersionUuid: string };

/** Stage-C AREA rollup payload (decreasing priority) for the latest graph. */
export type BprGraphAreaPrioritiesResult = BprGraphAreaPriorityResult & {
  graphVersionUuid: string;
};

/** Result of a full diagram generation (cached batch or freshly generated). */
export interface BprGenerateDiagramsResult {
  diagrams: BprDiagramRecord[];
  cached: boolean;
  /** Sections whose model output failed validation; the rest of the batch persisted. */
  failedSectionKeys: string[];
}

/** Result of a per-section future-state refresh (the new full batch). */
export interface BprRefreshSectionDiagramsResult {
  diagrams: BprDiagramRecord[];
  refreshedSectionKeys: string[];
  /** Sections whose model output failed validation; their specs carried forward unchanged. */
  failedSectionKeys: string[];
}

/** Result of a section process-flow top-up (the new full batch). */
export interface BprEnsureSectionDiagramsResult {
  diagrams: BprDiagramRecord[];
  addedSectionKeys: string[];
  /** Sections whose model output failed validation; they were not added this round. */
  failedSectionKeys: string[];
}

/** Poll payload of an async diagram run (GET /diagram-runs/:runId, unwrapped). */
export type BprDiagramRunPollResult =
  | { status: 'running'; mode?: string | null }
  | { status: 'failed'; error: string }
  | {
      status: 'completed';
      mode?: string | null;
      diagrams: BprDiagramRecord[];
      refreshedSectionKeys?: string[];
      addedSectionKeys?: string[];
      failedSectionKeys?: string[];
    };

async function get<T>(path: string): Promise<T> {
  const res = await apiFetch<{ data: T }>(`${BASE}${path}`);
  return res.data;
}

async function send<T>(path: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
  const res = await apiFetch<{ data: T }>(`${BASE}${path}`, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  return res.data;
}

// ── Async diagram runs (submit + 202 + poll) ────────────────────────────────
// A fresh diagram generation can outlive Cloud Run's request timeout, so the
// three mutation endpoints answer 202 { runId } on a cache/no-op miss and the
// client polls. The protocol is confined to this transport layer: the bprApi
// mutation functions below await the run to completion, so callers keep plain
// awaited-promise semantics (the deal store's per-deal queue still serializes).

const DIAGRAM_RUN_POLL_INTERVAL_MS = 3_000;
// Absolute cap, kept BELOW the server's 15 min stale-run valve so the client
// times out with a clean message before the server declares the run dead.
const DIAGRAM_RUN_POLL_CAP_MS = 10 * 60 * 1000;

/** 202 acknowledgement of an async diagram run. */
interface BprDiagramRunAck {
  runId: number;
  runReused: boolean;
}

function isRunAck(res: unknown): res is BprDiagramRunAck {
  return typeof res === 'object' && res !== null && 'runId' in res;
}

async function awaitDiagramRun(
  runId: number
): Promise<Extract<BprDiagramRunPollResult, { status: 'completed' }>> {
  const startedAt = Date.now();
  for (;;) {
    const result = await bprApi.pollDiagramRun(runId);
    if (result.status === 'completed') return result;
    if (result.status === 'failed') throw new Error(result.error);
    if (Date.now() - startedAt > DIAGRAM_RUN_POLL_CAP_MS) {
      throw new Error('BPR_DIAGRAM_RUN_TIMED_OUT');
    }
    await new Promise<void>((resolve) => setTimeout(resolve, DIAGRAM_RUN_POLL_INTERVAL_MS));
  }
}

export const bprApi = {
  // ── Engagements ─────────────────────────────────────────────────────────

  async listEngagements(): Promise<BprEngagement[]> {
    return await get('/engagements');
  },

  async createEngagement(input: BprCreateEngagementRequest): Promise<BprEngagement> {
    return await send('/engagements', 'POST', input);
  },

  async getEngagement(engagementUuid: string): Promise<BprEngagement> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}`);
  },

  /** The engagement auto-created for a deal_category='bpr' deal; null when none exists. */
  async getEngagementByDeal(dealUuid: string): Promise<BprEngagement | null> {
    try {
      return await get(`/engagements/by-deal/${encodeURIComponent(dealUuid)}`);
    } catch {
      return null;
    }
  },

  async updateEngagement(
    engagementUuid: string,
    input: BprUpdateEngagementRequest
  ): Promise<BprEngagement> {
    return await send(`/engagements/${encodeURIComponent(engagementUuid)}`, 'PATCH', input);
  },

  /** Pin the workspace's current effective framework onto the engagement. */
  async pinFramework(engagementUuid: string): Promise<BprPinFrameworkResult> {
    return await send(`/engagements/${encodeURIComponent(engagementUuid)}/pin-framework`, 'POST');
  },

  /** The framework snapshot frozen at pin time (null when not pinned yet). */
  async getPinnedFramework(engagementUuid: string): Promise<BprFrameworkContent | null> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/framework`);
  },

  // ── Sessions ────────────────────────────────────────────────────────────

  async listSessions(engagementUuid: string): Promise<BprSession[]> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/sessions`);
  },

  async createSession(
    engagementUuid: string,
    input: BprCreateSessionRequest
  ): Promise<BprSession> {
    return await send(`/engagements/${encodeURIComponent(engagementUuid)}/sessions`, 'POST', input);
  },

  async updateSession(
    engagementUuid: string,
    sessionUuid: string,
    input: BprUpdateSessionRequest
  ): Promise<BprSession> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/sessions/${encodeURIComponent(sessionUuid)}`,
      'PATCH',
      input
    );
  },

  async attachTranscript(
    engagementUuid: string,
    sessionUuid: string,
    transcriptDocumentUuid: string
  ): Promise<BprSession> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/sessions/${encodeURIComponent(sessionUuid)}/attach-transcript`,
      'POST',
      { transcriptDocumentUuid }
    );
  },

  /**
   * Upload a transcript file to a session - same pipeline and format gates as
   * the deal transcript upload (.txt/.pdf/.docx/.vtt/.srt/.json, content-hash
   * dedup, background embed). Also lands the text as a transcript-sourced
   * discovery note for synthesis.
   */
  async uploadSessionTranscript(
    engagementUuid: string,
    sessionUuid: string,
    file: File,
    force = false
  ): Promise<{ session: BprSession; docUuid: string; reused?: boolean }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const qs = force ? '?force=true' : '';
    const result = await apiFetch<{
      data: { session: BprSession; docUuid: string; reused?: boolean };
    }>(
      `${BASE}/engagements/${encodeURIComponent(engagementUuid)}/sessions/${encodeURIComponent(sessionUuid)}/transcript${qs}`,
      { method: 'POST', body: formData }
    );
    return result.data;
  },

  // ── Knowledge graph (Stage A+B cache + Stage C/D1 reads) ────────────────

  /** Latest cached graph version for the engagement; null when none built. */
  async getLatestGraph(engagementUuid: string): Promise<BprGraphVersionDetail | null> {
    try {
      return await get(`/engagements/${encodeURIComponent(engagementUuid)}/graph`);
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * Build (or reuse) the knowledge graph over the engagement's ingested
   * transcripts. Long-running on a fresh build (one model call); served from
   * the cache when the transcript set is unchanged unless force is set.
   */
  async buildGraph(engagementUuid: string, force = false): Promise<BprGraphVersionDetail> {
    return await send(`/engagements/${encodeURIComponent(engagementUuid)}/graph`, 'POST', {
      force,
    });
  },

  /** Deterministic Critical/High/Medium/Low tiers over the latest graph. */
  async getGraphPriorities(engagementUuid: string): Promise<BprGraphPrioritiesResult | null> {
    try {
      return await get(`/engagements/${encodeURIComponent(engagementUuid)}/graph/priorities`);
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * The finding priorities rolled up per functional area, decreasing priority
   * order - drives section-level ordering (the deal Prioritization tab).
   * Null when no graph has been built yet.
   */
  async getGraphAreaPriorities(
    engagementUuid: string
  ): Promise<BprGraphAreaPrioritiesResult | null> {
    try {
      return await get(
        `/engagements/${encodeURIComponent(engagementUuid)}/graph/area-priorities`
      );
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /** The render-agnostic D1 diagram model (system_view + priority_view). */
  async getGraphDiagram(engagementUuid: string): Promise<BprGraphDiagramResult | null> {
    try {
      return await get(`/engagements/${encodeURIComponent(engagementUuid)}/graph/diagram`);
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  // ── Diagram specs (the visual output layer) ─────────────────────────────

  /** Newest cached diagram batch, section-nav order (architecture first); [] when none. */
  async getDiagrams(engagementUuid: string): Promise<BprDiagramRecord[]> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/diagrams`);
  },

  /**
   * Generate (or reuse) the diagram batch for the engagement's latest graph.
   * Served synchronously from the cache while it still covers the latest
   * graph unless force; a fresh generation is an async run (202 + poll,
   * awaited to completion here - can take minutes).
   */
  async generateDiagrams(
    engagementUuid: string,
    force = false
  ): Promise<BprGenerateDiagramsResult> {
    const res = await apiFetch<{ data: BprDiagramRecord[]; cached?: boolean } | BprDiagramRunAck>(
      `${BASE}/engagements/${encodeURIComponent(engagementUuid)}/diagrams`,
      { method: 'POST', body: JSON.stringify({ force }) }
    );
    if (!isRunAck(res)) {
      return { diagrams: res.data, cached: res.cached ?? true, failedSectionKeys: [] };
    }
    const completed = await awaitDiagramRun(res.runId);
    return {
      diagrams: completed.diagrams,
      cached: false,
      failedSectionKeys: completed.failedSectionKeys ?? [],
    };
  },

  /**
   * Regenerate ONLY the future-state workflows of the named questionnaire
   * sections (fired after the user saves changes in them); every other spec
   * of the newest batch carries forward. Sections without a workflow diagram
   * are skipped server-side (refreshedSectionKeys reports what actually ran).
   * A real refresh is an async run (202 + poll, awaited to completion here).
   */
  async refreshSectionFutureState(
    engagementUuid: string,
    sectionKeys: string[]
  ): Promise<BprRefreshSectionDiagramsResult> {
    const res = await apiFetch<
      { data: BprDiagramRecord[]; refreshedSectionKeys?: string[] } | BprDiagramRunAck
    >(`${BASE}/engagements/${encodeURIComponent(engagementUuid)}/diagrams/sections/refresh`, {
      method: 'POST',
      body: JSON.stringify({ sectionKeys }),
    });
    if (!isRunAck(res)) {
      return {
        diagrams: res.data,
        refreshedSectionKeys: res.refreshedSectionKeys ?? [],
        failedSectionKeys: [],
      };
    }
    const completed = await awaitDiagramRun(res.runId);
    return {
      diagrams: completed.diagrams,
      refreshedSectionKeys: completed.refreshedSectionKeys ?? [],
      failedSectionKeys: completed.failedSectionKeys ?? [],
    };
  },

  /**
   * Create the process-flow pairs for questionnaire sections MISSING from the
   * newest diagram batch (their questions were captured after the batch was
   * minted); every existing spec carries forward. Sections already covered
   * are skipped server-side (addedSectionKeys reports what actually ran).
   * A real top-up is an async run (202 + poll, awaited to completion here).
   */
  async ensureSectionDiagrams(
    engagementUuid: string,
    sectionKeys: string[]
  ): Promise<BprEnsureSectionDiagramsResult> {
    const res = await apiFetch<
      { data: BprDiagramRecord[]; addedSectionKeys?: string[] } | BprDiagramRunAck
    >(`${BASE}/engagements/${encodeURIComponent(engagementUuid)}/diagrams/sections/ensure`, {
      method: 'POST',
      body: JSON.stringify({ sectionKeys }),
    });
    if (!isRunAck(res)) {
      return {
        diagrams: res.data,
        addedSectionKeys: res.addedSectionKeys ?? [],
        failedSectionKeys: [],
      };
    }
    const completed = await awaitDiagramRun(res.runId);
    return {
      diagrams: completed.diagrams,
      addedSectionKeys: completed.addedSectionKeys ?? [],
      failedSectionKeys: completed.failedSectionKeys ?? [],
    };
  },

  /** One poll of an async diagram run (unwrapped payload, no data envelope). */
  async pollDiagramRun(runId: number): Promise<BprDiagramRunPollResult> {
    return await apiFetch<BprDiagramRunPollResult>(`${BASE}/diagram-runs/${runId}`);
  },

  // ── Discovery notes ─────────────────────────────────────────────────────

  async listNotes(engagementUuid: string, sessionUuid?: string): Promise<BprDiscoveryNote[]> {
    const qs = sessionUuid ? `?sessionUuid=${encodeURIComponent(sessionUuid)}` : '';
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/notes${qs}`);
  },

  async createNote(
    engagementUuid: string,
    sessionUuid: string,
    input: BprCreateNoteRequest
  ): Promise<BprDiscoveryNote> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/sessions/${encodeURIComponent(sessionUuid)}/notes`,
      'POST',
      input
    );
  },

  // ── Findings ────────────────────────────────────────────────────────────

  async listFindings(engagementUuid: string): Promise<BprFinding[]> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/findings`);
  },

  async createFinding(
    engagementUuid: string,
    input: BprCreateFindingRequest
  ): Promise<BprFinding> {
    return await send(`/engagements/${encodeURIComponent(engagementUuid)}/findings`, 'POST', input);
  },

  async updateFinding(
    engagementUuid: string,
    findingUuid: string,
    input: BprUpdateFindingRequest
  ): Promise<BprFinding> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/findings/${encodeURIComponent(findingUuid)}`,
      'PATCH',
      input
    );
  },

  // ── Recommendations ─────────────────────────────────────────────────────

  async listRecommendations(engagementUuid: string): Promise<BprRecommendation[]> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/recommendations`);
  },

  async createRecommendation(
    engagementUuid: string,
    input: BprCreateRecommendationRequest
  ): Promise<BprRecommendation> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/recommendations`,
      'POST',
      input
    );
  },

  async updateRecommendation(
    engagementUuid: string,
    recommendationUuid: string,
    input: BprUpdateRecommendationRequest
  ): Promise<BprRecommendation> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/recommendations/${encodeURIComponent(recommendationUuid)}`,
      'PATCH',
      input
    );
  },

  // ── Outputs ─────────────────────────────────────────────────────────────

  async listOutputs(engagementUuid: string): Promise<BprOutput[]> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/outputs`);
  },

  async generateOutput(engagementUuid: string, kind: BprOutputKind): Promise<BprOutput> {
    return await send(
      `/engagements/${encodeURIComponent(engagementUuid)}/outputs/${encodeURIComponent(kind)}/generate`,
      'POST'
    );
  },

  // ── Framework store ─────────────────────────────────────────────────────

  async listFrameworkVersions(toolKey?: string): Promise<BprFrameworkVersionSummary[]> {
    const qs = toolKey ? `?toolKey=${encodeURIComponent(toolKey)}` : '';
    return await get(`/framework/versions${qs}`);
  },

  async getFrameworkVersion(versionUuid: string): Promise<BprFrameworkVersionDetail> {
    return await get(`/framework/versions/${encodeURIComponent(versionUuid)}`);
  },

  /** Workspace-admin only: approve a draft version at the trust gate. */
  async approveFrameworkVersion(versionUuid: string): Promise<BprFrameworkVersionSummary> {
    return await send(`/framework/versions/${encodeURIComponent(versionUuid)}/approve`, 'POST');
  },

  /** Workspace-admin only: reject a draft version (optional comment). */
  async rejectFrameworkVersion(
    versionUuid: string,
    comment?: string
  ): Promise<BprFrameworkVersionSummary> {
    return await send(
      `/framework/versions/${encodeURIComponent(versionUuid)}/reject`,
      'POST',
      comment ? { comment } : {}
    );
  },

  async recordFrameworkFeedback(
    versionUuid: string,
    input: BprFeedbackRequest
  ): Promise<{ recorded: boolean }> {
    return await send(
      `/framework/versions/${encodeURIComponent(versionUuid)}/feedback`,
      'POST',
      input
    );
  },

  /** Workspace-admin only: dispatch AI generation of a new framework version (202). */
  async generateFramework(
    input: BprGenerateFrameworkRequest
  ): Promise<BprGenerateFrameworkResult> {
    return await send('/framework/generate', 'POST', input);
  },

  // ── Overrides ───────────────────────────────────────────────────────────

  async listOverrides(toolKey?: string): Promise<BprOverride[]> {
    const qs = toolKey ? `?toolKey=${encodeURIComponent(toolKey)}` : '';
    return await get(`/framework/overrides${qs}`);
  },

  async createOverride(input: BprCreateOverrideRequest): Promise<BprOverride> {
    return await send('/framework/overrides', 'POST', input);
  },

  async retireOverride(overrideUuid: string): Promise<{ retired: boolean }> {
    return await send(`/framework/overrides/${encodeURIComponent(overrideUuid)}`, 'DELETE');
  },

  // ── Sources ─────────────────────────────────────────────────────────────

  async listSources(toolKey?: string): Promise<BprSource[]> {
    const qs = toolKey ? `?toolKey=${encodeURIComponent(toolKey)}` : '';
    return await get(`/framework/sources${qs}`);
  },

  async registerSource(input: BprRegisterSourceRequest): Promise<BprSource> {
    return await send('/framework/sources', 'POST', input);
  },

  async updateSource(sourceUuid: string, input: BprUpdateSourceRequest): Promise<BprSource> {
    return await send(`/framework/sources/${encodeURIComponent(sourceUuid)}`, 'PATCH', input);
  },

  // ── Effective framework (resolution preview without pinning) ────────────

  async getEffectiveFramework(toolKey: string): Promise<BprResolvedFramework | null> {
    return await get(`/framework/effective?toolKey=${encodeURIComponent(toolKey)}`);
  },

  // ── Synthesis runs (Plan 47 C1) ──────────────────────────────────────────

  async listSynthesisRuns(engagementUuid: string): Promise<BprSynthesisRunSummary[]> {
    return await get(`/engagements/${encodeURIComponent(engagementUuid)}/synthesis-runs`);
  },
};
